<template>
  <button
    :type="type"
    :disabled="disabled"
    :class="clsx('orange-fancy-button', { 'orange-fancy-button--disabled': disabled })"
    @click="handleClick"
    v-on="$listeners"
  >
    <component
      v-if="hasLink"
      :is="linkComponent"
      class="orange-fancy-button__link"
      v-bind="linkProps"
    >
      <slot />
    </component>
    <span class="orange-fancy-button__content" v-else>
      <slot />
    </span>
  </button>
</template>

<script>
import clsx from 'clsx';
import { HashAnchor, SmartLink } from '@/shared/ui/link';

export default {
  props: {
    type: {
      type: String,
      default: 'button',
      validator(value) { return ['button', 'submit', 'reset'].includes(value); },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    url: {
      type: String,
      default: null,
    },
    route: {
      type: [Object, String],
      default: null,
    },
    target: {
      type: String,
      default: '_self',
      validator(value) { return ['_blank', '_self', '_parent', '_top'].includes(value); },
    },
    method: {
      type: Function,
      default: null,
    },
    event: {
      type: String,
      default: null,
    },
    hash: {
      type: String,
      default: null,
    },
  },
  methods: {
    clsx,
    handleClick(event) {
      if (this.disabled) {
        event.preventDefault();
        return;
      }

      if (this.method) {
        this.method(event);
      }

      if (this.event) {
        this.$eventBus.$emit(this.event, event);
      }

      this.$emit('click', event);
    },
  },
  components: { SmartLink, HashAnchor },
  computed: {
    hasLink() {
      return this.url || this.route || this.hash;
    },
    linkComponent() {
      if (this.hash && !this.url && !this.route) {
        return 'HashAnchor';
      }

      return 'SmartLink';
    },
    linkProps() {
      const props = {
        disabled: this.disabled,
      };
      if (this.linkComponent === 'HashAnchor') {
        return {
          ...props,
          hash: this.hash,
        };
      } else {
        return {
          ...props,
          url: this.url,
          route: this.route,
          target: this.target,
          method: this.method,
          event: this.event,
          hash: this.hash,
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.orange-fancy-button {
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(90deg, #F6AB6F 0%, #EB883B 100%);
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #FFFFFF;
  border-radius: 16px;
  width: 100%;
  border: none;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover:not(.orange-fancy-button--disabled) {
    background: linear-gradient(90deg, #e59455 0%, #d67020 100%);
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__link {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #FFFFFF;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px 24px;
  }

  &__content {
    padding: 16px 24px;
  }
}
</style>
